import {useAuth} from '@/modules/auth/api'
import {SequenceFooter} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/sequence-footer'
import {PastTherapyDateAlert} from '@/modules/patients-therapies/components/past-therapy-date-alert'
import {PatientTherapySequenceSummary} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/sequence-summary'
import {AboutTab} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/tabs/about'
import {HistoryTab} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/tabs/activity'
import {ObservationsTab} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/tabs/observations'
import {ProtocolTab} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/tabs/protocol'
import {getSequenceActiveSubstancePreparationType} from '@/modules/patients-therapies/functions'
import {useTherapySequenceActions, type Action} from '@/modules/patients-therapies/hooks'
import {ParameterSchema} from '@/modules/patients-therapies/schemas'
import {useGetSettings} from '@/modules/settings/api'
import {useGetUser} from '@/modules/users/api'
import {usePatientTherapySequenceContext} from '@/shared/contexts/patient-therapy-sequence-context'
import {Box, Tabs} from '@chakra-ui/react'
import {PatientTherapySequenceEvent, PatientTherapySequenceMachine} from '@cypro/engine/mutators'
import {DrawerBody, DrawerHeader} from '@cypro/ui/components'
import {focusFirstErrorField, useAppForm} from '@cypro/ui/integrations/form'
import {revalidateLogic} from '@tanstack/react-form'
import {useState} from 'react'

export type NoteKind = 'therapy' | 'pharmacy'

export interface PatientTherapySequenceDrawerProps {
  defaultTab?: string
  openPrescribedName?: boolean
}

export function PatientTherapySequenceDrawer(props: PatientTherapySequenceDrawerProps) {
  const {sequence} = usePatientTherapySequenceContext()
  const {auth} = useAuth()
  const [user] = useGetUser(auth?.user?.id || '')
  const {settings} = useGetSettings()

  return (
    <PatientTherapySequenceDrawerContent
      key={PatientTherapySequenceMachine.buildKey({user, sequence, settings})}
      {...props}
    />
  )
}

function PatientTherapySequenceDrawerContent({
  defaultTab = 'about',
  openPrescribedName,
}: PatientTherapySequenceDrawerProps) {
  const {sequence} = usePatientTherapySequenceContext()
  const {sendEvent} = useTherapySequenceActions(sequence)
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [selectedNote, setSelectedNote] = useState<NoteKind | null>(null)

  function onSelectNote(kind: NoteKind) {
    setSelectedNote(kind)
    // the alert lives at the top of the about tab — switch to it so it's visible
    setActiveTab('about')
  }

  const form = useAppForm({
    defaultValues: {
      carrierSolutionId: sequence.carrierSolutionId || undefined,
      applicationFormId: sequence.applicationFormId || '',
      duration: sequence.duration || 0,
      volume: sequence.volume || undefined,
      volumeMode: sequence.volumeMode || undefined,
      preparationType: sequence.preparationType || getSequenceActiveSubstancePreparationType(sequence) || undefined,
      medicine: sequence.medicine?.map(m => ({id: m.id, prescribedNameId: m.prescribedNameId || undefined})) || [],
      consumables: sequence.consumables?.map(c => ({id: c.consumableId, quantity: c.quantity})) || [],
    } as ParameterSchema,
    validators: {
      onSubmit: ParameterSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmitInvalid() {
      focusFirstErrorField()
    },
    onSubmit: async props => {
      if (sequence.status !== 'validating' || sequence.pharmacistValidatedAt) return
      try {
        await sendEvent({id: sequence.id, event: {type: 'PHARMACIST_VALIDATE', body: props.value}})
      } catch (error) {
        console.error('Failed to validate therapy sequence', error)
      }
    },
  })

  async function onSubmit(action: Action) {
    if (action === 'PHARMACIST_VALIDATE') {
      await form.handleSubmit()
    } else {
      await sendEvent({id: sequence.id, event: {type: action} as PatientTherapySequenceEvent})
    }
  }

  return (
    <>
      <Tabs.Root
        value={activeTab}
        onValueChange={({value}) => setActiveTab(value)}
        lazyMount
        display="flex"
        flexDirection="column"
        minHeight="0"
        width="100%"
        size="sm"
      >
        <DrawerHeader>
          <PatientTherapySequenceSummary onOpenNote={onSelectNote} openNote={selectedNote} />
        </DrawerHeader>

        <DrawerBody>
          <Box minHeight="0" display="flex" flexDirection="column" width="100%">
            <PastTherapyDateAlert />
            <AboutTab
              form={form}
              openNote={selectedNote}
              onDismissNote={() => setSelectedNote(null)}
              openPrescribedName={openPrescribedName}
            />
            <ProtocolTab />
            <ObservationsTab />
            <HistoryTab />
          </Box>
        </DrawerBody>
      </Tabs.Root>

      <SequenceFooter form={form} onSubmit={onSubmit} />
    </>
  )
}
